import { Anchor, Badge, Flex, Text } from '@mantine/core';
import { Link } from 'react-router-dom';
import type { DealEventTct } from '@dealstack/shared/lib/backendClient';
import { DATE_FORMATS, formatStringDateToUI } from '@dealstack/shared/lib/format/dates/date.ts';
import {
	useTranslationCapManage,
	useTranslationCommonUi,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import {
	formatDealEventHeadingParts,
	formatAccessSummary,
	formatActorName,
	formatAuthSummary,
	formatCapitalParties,
	formatCapitalSummary,
	formatCategoryLabel,
	formatChangedFields,
	formatHoldingParties,
	formatHoldingSummary,
	holdingDetailsPath,
} from '@dealstack/frontend/app/pages/Project/DealEventLog/dealEventLog.utils.ts';

interface Props {
	event: DealEventTct;
	dealId: string;
}

export const DealEventRow = ({ event, dealId }: Props) => {
	const tCapManage = useTranslationCapManage();
	const tCommonUi = useTranslationCommonUi();
	const accessSummary = event.access && formatAccessSummary(event.access, tCommonUi);
	const authSummary = event.auth && formatAuthSummary(event.auth, tCapManage);
	const heading = formatDealEventHeadingParts(event, tCapManage);
	const changedFields = formatChangedFields(event, tCapManage);
	const capitalSummary = event.capital && formatCapitalSummary(event.capital, tCapManage);
	const capitalParties = event.capital && formatCapitalParties(event.capital);
	const { holding } = event;
	const holdingParties = holding && formatHoldingParties(holding, tCapManage);

	return (
		<Flex direction="column" gap="0.25rem" py="0.75rem">
			<Flex gap="0.5rem" align="center" wrap="wrap">
				<Text fw={600} size="sm">
					{heading.label}
				</Text>
				{!!heading.action && <Text size="sm">{heading.action}</Text>}
				<Badge size="sm" variant="light" color="gray">
					{formatCategoryLabel(event.category, tCapManage)}
				</Badge>
			</Flex>

			<Text size="sm" c="dimmed">
				{formatActorName(event.actor, tCapManage)}
				{' · '}
				{formatStringDateToUI(event.occurredAt, DATE_FORMATS.TIMELINE_DATE_TIME)}
			</Text>

			{!!accessSummary && <Text size="sm">{accessSummary}</Text>}

			{!!authSummary && <Text size="sm">{authSummary}</Text>}

			{!!capitalSummary && <Text size="sm">{capitalSummary}</Text>}

			{!!capitalParties && (
				<Text size="sm" c="dimmed">
					{capitalParties}
				</Text>
			)}

			{holding && (
				<>
					<Text size="sm">{formatHoldingSummary(holding, tCapManage)}</Text>
					{!!holdingParties && (
						<Text size="sm" c="dimmed">
							{holdingParties}
						</Text>
					)}
					{!!holding.note && (
						<Text size="sm" c="dimmed" lineClamp={2}>
							{holding.note}
						</Text>
					)}
					{!holding.deleted && (
						<Anchor
							component={Link}
							to={holdingDetailsPath(dealId, holding)}
							size="sm"
							style={{ alignSelf: 'flex-start' }}
						>
							{tCapManage('deal_event_log.holding.view')}
						</Anchor>
					)}
				</>
			)}

			{!!changedFields && (
				<Text size="sm" c="dimmed">
					{changedFields}
				</Text>
			)}
		</Flex>
	);
};
